/**
 * userAccess.utils.ts
 * Pure utility functions for the User Access feature.
 * No React imports — safe to use in hooks, services, and components alike.
 */
import type { CognitoUser } from '@/services/cognitoUserManagement.service';

// ─── Email Validation ────────────────────────────────────────────────────────

/**
 * String-based email check — no regex, no backtracking.
 * Verifies: non-empty local-part, exactly one '@',
 * a dot in the domain, and a non-empty TLD.
 */
export const isValidEmail = (email: string): boolean => {
  const atIndex = email.indexOf('@');
  if (atIndex <= 0) return false;
  const domain = email.slice(atIndex + 1);
  const dotIndex = domain.lastIndexOf('.');
  return dotIndex > 0 && dotIndex < domain.length - 1;
};

// ─── CSV Export ──────────────────────────────────────────────────────────────

/**
 * Escapes a CSV field to prevent formula injection attacks.
 * Prefixes cells starting with =, +, -, @ with a single quote.
 */
const escapeCsvField = (value: string): string => {
  if (!value) return value;
  const firstChar = value.charAt(0);
  if (firstChar === '=' || firstChar === '+' || firstChar === '-' || firstChar === '@') {
    return `'${value}`;
  }
  return value;
};

/**
 * Triggers a browser download of the given user list as a CSV file.
 * Only exports the columns that are reliably populated by the Lambda: Email and Status.
 * Escapes fields to prevent CSV/Excel formula injection.
 */
export const exportUsersToCSV = (users: CognitoUser[]): void => {
  const csv = [
    'Email,Status',
    ...users.map((u) => `${escapeCsvField(u.email)},${escapeCsvField(u.status)}`)
  ].join('\n');
  const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  const link = Object.assign(document.createElement('a'), {
    href:     url,
    download: `user_emails_${new Date().toISOString().split('T')[0]}.csv`,
  });
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
